import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Markdown } from '../chat/Markdown';

// Matches a task identifier token like TASK-123 or ABC-45 (uppercase prefix +
// number). Used to turn action items that reference created Tasks into
// clickable navigation when we know the owning project. The global variant
// drives split(); a separate anchored variant tests each token without the
// stateful `lastIndex` pitfall of a shared global regex.
const TASK_ID_SPLIT_RE = /\b([A-Z][A-Z0-9]{1,9}-\d+)\b/g;
const TASK_ID_TEST_RE = /^[A-Z][A-Z0-9]{1,9}-\d+$/;

/**
 * Render action-items markdown. When a `projectId` is supplied (project-owned
 * notes) any task identifiers (e.g. TASK-42) are linked to the task detail
 * route so meeting action items navigate to their created Tasks (CLE-205
 * acceptance: "Action items link to created Tasks (clickable)").
 *
 * Without a projectId we fall back to plain markdown rendering — leads link
 * their action items to LeadActions in the same surface, and clients have no
 * task route.
 */
export function ActionItems({ content, projectId }: { content: string; projectId?: string }) {
  if (!projectId) {
    return <Markdown content={content} />;
  }

  // Render line-by-line so we can splice task links into otherwise-plain text
  // while keeping list semantics. We special-case bullet lines, which is how
  // the backend pipeline emits action items.
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const bulletItems: { raw: string }[] = [];
  for (const line of lines) {
    const m = /^\s*[-*]\s+(.*)$/.exec(line);
    if (m) bulletItems.push({ raw: m[1] });
  }

  // If the content isn't a simple bullet list, keep markdown but still link ids.
  if (bulletItems.length === 0) {
    return (
      <div className="space-y-2 text-sm leading-relaxed text-gray-700">
        {lines.map((line, i) => (
          <p key={`ai-${i}`}>{linkifyTasks(line, `ai-${i}`)}</p>
        ))}
      </div>
    );
  }

  return (
    <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
      {bulletItems.map((it, i) => (
        <li key={`ai-${i}`}>{linkifyTasks(it.raw, `ai-${i}`)}</li>
      ))}
    </ul>
  );
}

function linkifyTasks(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(TASK_ID_SPLIT_RE);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (TASK_ID_TEST_RE.test(part)) {
      return (
        <Link
          key={key}
          to={`/tasks/${encodeURIComponent(part)}`}
          className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
        >
          {part}
        </Link>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}
